import styled from "@emotion/styled";
import { Skeleton } from "@/components/ui/feedback/Skeleton";
import type { TableSkeletonProps } from "./DataTable.types";

/* Exact compiled output of the pre-migration Tailwind utilities. Widths
 * cycle through the original w-* scale values; the `&&` override reproduces
 * the old tailwind-merge resolution against Skeleton's own height class. */

const skeletonWidths = ["6rem", "8rem", "5rem", "9rem", "7rem", "10rem", "4rem"] as const;

const TableSkeletonCell = styled("td", {
  label: "AgenticDataTable-skeletonCell"
})({
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  verticalAlign: "middle",
  height: "4rem",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
});

const TableSkeletonLines = styled("div", {
  label: "AgenticDataTable-skeletonLines"
})({
  display: "flex",
  minWidth: "0px",
  flexDirection: "column",
  gap: ".5rem"
});

const TableSkeletonLine = styled(Skeleton, {
  label: "AgenticDataTable-skeletonLine",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { height: string; width: string; faded?: boolean } }>(({ ownerState }) => [
  {
    "&&": {
      height: ownerState.height,
      width: ownerState.width
    }
  },
  ownerState.faded && { opacity: ".7" }
]);

export function TableSkeleton({ rows = 5, columns = 4, colSpan }: TableSkeletonProps) {
  const cellCount = Math.max(1, colSpan ?? columns);

  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex} aria-hidden="true">
          {Array.from({ length: cellCount }).map((__, columnIndex) => {
            const width = skeletonWidths[(rowIndex + columnIndex) % skeletonWidths.length];
            const showSecondaryLine = columnIndex === 0 || (columnIndex + rowIndex) % 4 === 0;

            return (
              <TableSkeletonCell key={columnIndex}>
                <TableSkeletonLines>
                  <TableSkeletonLine
                    variant="text"
                    ownerState={{ height: ".875rem", width: columnIndex === cellCount - 1 ? "3.5rem" : width }}
                  />
                  {showSecondaryLine && (
                    <TableSkeletonLine
                      variant="text"
                      ownerState={{ height: ".625rem", width: columnIndex === 0 ? "7rem" : "4rem", faded: true }}
                    />
                  )}
                </TableSkeletonLines>
              </TableSkeletonCell>
            );
          })}
        </tr>
      ))}
    </>
  );
}
